<template>
  <span :data-testid="`${sectionTag}-wrapper`">
    <WFlyout
      :components="items"
      :position="position"
      :disabled="isSending"
      :section-tag="sectionTag"
      @item-clicked="handleChoice"
    >
      <template v-if="$slots.activator" #activator="{ props: activatorProps }">
        <slot name="activator" :props="activatorProps" />
      </template>
      <template #item-renderer="{ props: { item } }">
        <FleetMenuItemLabel :title="item.title" :reason="reasonOf(item)" :hover="hoverOf(item)" />
      </template>
    </WFlyout>
  </span>
  <WModal
    v-model="isStopDialogOpen"
    :head-line="stopText.title"
    :action-type="Actions.CancelConfirm"
    :confirm-text="stopConfirmText"
    :confirm-disabled="!stopRunId"
    :allow-icon="false"
    max-width="480px"
    :section-tag="`${sectionTag}-stop-dialog`"
    @confirm="handleStopConfirm"
    @cancel="isStopDialogOpen = false"
  >
    <template #content>
      <div class="fleet-runner-stop">
        <p>{{ stopText.message }}</p>
        <WDropdown
          v-model="stopRunId"
          label="Run"
          :items="runOptions"
          item-title="title"
          item-value="value"
          :section-tag="`${sectionTag}-stop-run`"
        />
      </div>
    </template>
  </WModal>
  <FleetRunnerSlotsDialog
    v-model="isSlotsDialogOpen"
    :runner="runner"
    :owner-name="ownerName"
    :section-tag="`${sectionTag}-slots-dialog`"
    @confirm="handleSlotsConfirm"
  />
  <FleetRunnerMachineSettingsDialog
    v-model="isSettingsDialogOpen"
    :runner="runner"
    :owner-name="ownerName"
    :section-tag="`${sectionTag}-settings-dialog`"
    @confirm="handleSettingsConfirm"
  />
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

import { useAuthStore } from '@/stores/auth.store'
import { useClaudeFleetStore } from '@/stores/claude-fleet.store'
import { useClaudeFleetRunnersStore } from '@/stores/claude-fleet-runners.store'

import { useConfirm } from '@/composables/useConfirm'
import { useToast } from '@/composables/useToast'

import {
  FLEET_RUNNER_CONFIRMED_CHOICES,
  FLEET_RUNNER_RUN_CHOICES,
  canActOnRunner,
  ownRunnerRemoteControl,
  runnerActionRequest,
  runnerChoiceText,
  runnerConfirmButton,
  runnerMenuItems,
  runnerRunningRuns
} from '@/services/claude-fleet'
import type {
  FleetRunner,
  FleetRunnerEditableSettings,
  FleetRunnerMenuChoice,
  FleetRunnerMenuItem,
  FleetRunnerSlotsValue
} from '@/services/claude-fleet'
import { Actions } from '@/constants/actions'

import WFlyout from '@wasabi/content-displays/WFlyout.vue'
import WModal from '@wasabi/content-displays/WModal.vue'
import WDropdown from '@wasabi/inputs/WDropdown.vue'

import FleetMenuItemLabel from './FleetMenuItemLabel.vue'
import FleetRunnerMachineSettingsDialog from './FleetRunnerMachineSettingsDialog.vue'
import FleetRunnerSlotsDialog from './FleetRunnerSlotsDialog.vue'

/** Opens to the left by default, from the end of a runner's row; the sidebar runner card, at the screen's left, opens it right. */
const props = withDefaults(defineProps<{ runner: FleetRunner; sectionTag: string; position?: 'left' | 'right' }>(), {
  position: 'left'
})

/** History opens the runner's drawer; the menu's owner decides what that means where it sits. */
const emit = defineEmits<{ history: [] }>()

const authStore = useAuthStore()
const fleetStore = useClaudeFleetStore()
const runnersStore = useClaudeFleetRunnersStore()

const { confirmDialog } = useConfirm()
const { showSuccess } = useToast()

const isPermitted = computed(() =>
  canActOnRunner(props.runner, { email: authStore.user?.email, operators: fleetStore.operators })
)

const isStopDialogOpen = ref(false)
const isSlotsDialogOpen = ref(false)
const isSettingsDialogOpen = ref(false)
const stopRunId = ref<string | null>(null)
/** Which of the two the run picker is for: Stop a run (for good) or Hand back a run (another runner runs it). */
const stopChoice = ref<FleetRunnerMenuChoice>('stop-run')

const isSending = computed(() => runnersStore.actionMachine === props.runner.machine)
const ownerName = computed(() => props.runner.name || props.runner.owner)
const remote = computed(() => ownRunnerRemoteControl(props.runner, authStore.user?.email))
const runningRuns = computed(() => runnerRunningRuns(props.runner, fleetStore.runs))
const items = computed(() =>
  runnerMenuItems(props.runner, {
    now: Date.now(),
    runningRuns: runningRuns.value.length,
    canAct: isPermitted.value,
    remote: remote.value
  })
)
const runOptions = computed(() =>
  runningRuns.value.map(run => ({ title: `${run.activity}${run.item ? ` · ${run.item}` : ''}`, value: run.runId }))
)
const stopText = computed(() => runnerChoiceText(stopChoice.value, ownerName.value))
const stopConfirmText = computed(() => runnerConfirmButton(stopChoice.value).confirmText)

/** WFlyout hands back its own item type, which knows nothing of the help text this menu adds. */
function menuItemOf(item: { key?: unknown }): FleetRunnerMenuItem | undefined {
  return items.value.find(menuItem => menuItem.key === item.key)
}

function reasonOf(item: { key?: unknown }): string | undefined {
  const menuItem = menuItemOf(item)
  return menuItem?.disabled ? menuItem.help : undefined
}

function hoverOf(item: { key?: unknown }): string | undefined {
  const menuItem = menuItemOf(item)
  return menuItem?.disabled ? undefined : menuItem?.help
}

async function send(
  choice: FleetRunnerMenuChoice,
  options: { runId?: string; value?: FleetRunnerSlotsValue; settings?: Partial<FleetRunnerEditableSettings> } = {}
) {
  const request = runnerActionRequest(choice, { ...options, byName: authStore.user?.name })
  const sent = await runnersStore.sendAction(props.runner.machine, request)
  if (sent) showSuccess(runnerChoiceText(choice, ownerName.value).done, ownerName.value)
}

async function handleChoice(item: FleetRunnerMenuItem) {
  if (item.key === 'locked') return
  if (item.key === 'remote') {
    if (remote.value) window.open(remote.value.url, '_blank', 'noopener,noreferrer')
    return
  }
  if (item.key === 'history') {
    emit('history')
    return
  }
  if (FLEET_RUNNER_RUN_CHOICES.includes(item.key)) {
    stopChoice.value = item.key
    stopRunId.value = runOptions.value[0]?.value ?? null
    isStopDialogOpen.value = true
    return
  }
  if (item.key === 'slots') {
    isSlotsDialogOpen.value = true
    return
  }
  if (item.key === 'settings') {
    isSettingsDialogOpen.value = true
    return
  }
  if (FLEET_RUNNER_CONFIRMED_CHOICES.includes(item.key)) {
    const text = runnerChoiceText(item.key, ownerName.value)
    const confirmed = await confirmDialog({ title: text.title, message: text.message, ...runnerConfirmButton(item.key) })
    if (!confirmed) return
  }
  await send(item.key)
}

async function handleStopConfirm() {
  isStopDialogOpen.value = false
  if (stopRunId.value) await send(stopChoice.value, { runId: stopRunId.value })
}

async function handleSlotsConfirm(value: FleetRunnerSlotsValue) {
  await send('slots', { value })
}

async function handleSettingsConfirm(settings: Partial<FleetRunnerEditableSettings>) {
  await send('settings', { settings })
}
</script>

<style scoped>
.fleet-runner-stop {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
</style>
